/**
 * Grafana Faro Web SDK Integration for Admin Panel (v2)
 *
 * Provides Real User Monitoring (RUM) for the admin panel:
 * - Performance metrics (Web Vitals with INP replacing FID)
 * - Error tracking (complementary to Sentry)
 * - Admin session tracking
 * - Distributed tracing (OpenTelemetry compatible)
 *
 * Environment Variables:
 * - VITE_GRAFANA_FARO_URL: Grafana Cloud Faro collector URL (optional, has default)
 * - VITE_ENVIRONMENT: Environment name (production/staging/development)
 * - VITE_VERSION: App version
 *
 * v2 Changes (2026-01-18):
 * - Migrated from getWebInstrumentations() to individual instrumentation classes
 * - ConsoleInstrumentation now takes disabledLevels directly
 * - WebVitalsInstrumentation now uses INP instead of FID
 * - Attribution data always collected (trackWebVitalsAttribution removed)
 *
 * @see https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/
 * @see https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/instrument/upgrading/upgrade-v2/
 */

import {
  initializeFaro,
  ConsoleInstrumentation,
  ErrorsInstrumentation,
  WebVitalsInstrumentation,
  SessionInstrumentation,
  ViewInstrumentation,
  LogLevel,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

let faroInstance = null;

/**
 * Initialize Grafana Faro for admin panel observability (v2 API)
 *
 * Call this at app startup (in main.jsx) after Sentry initialization.
 *
 * @returns {Object|null} Faro instance or null if not configured
 */
export const initFaro = () => {
  // Faro collector URL — routed through first-party proxy to bypass ad blockers.
  // The API key is embedded in the path and forwarded transparently.
  // In dev, Vite proxies /ingest to the Grafana collector so relative path works.
  // In staging/prod, Cloudflare Pages function handles the proxy.
  const ingestPath = '/ingest/fro/collect/b07fc76de371cc7cbd26099455868f7f';
  const faroUrl = import.meta.env.VITE_GRAFANA_FARO_URL || ingestPath;

  // Skip initialization if explicitly disabled
  if (import.meta.env.VITE_DISABLE_FARO === 'true') {
    console.info('[Faro Admin] Disabled via VITE_DISABLE_FARO');
    return null;
  }

  try {
    faroInstance = initializeFaro({
      // Grafana Cloud Faro collector endpoint (API key embedded in URL)
      url: faroUrl,

      // Application identification - distinct from webapp
      app: {
        name: 'impleo-admin',
        version: import.meta.env.VITE_VERSION || '1.0.0',
        environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      },

      // v2 Instrumentations - individual classes instead of getWebInstrumentations()
      instrumentations: [
        // Error tracking instrumentation
        new ErrorsInstrumentation(),

        // Web Vitals with INP (replaces FID in v2)
        new WebVitalsInstrumentation(),

        // Console capture with level filtering (v2: disabledLevels moved here)
        new ConsoleInstrumentation({
          disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE],
        }),

        // Session tracking
        new SessionInstrumentation(),

        // View/navigation tracking
        new ViewInstrumentation(),

        // OpenTelemetry distributed tracing
        // Note: Only propagate trace headers to backends that explicitly allow them in CORS
        // Otherwise the traceparent/tracestate headers cause preflight failures
        new TracingInstrumentation({
          instrumentationOptions: {
            // Propagate trace context ONLY to backends that allow trace headers
            propagateTraceHeaderCorsUrls: [
              // Local development (no CORS restrictions)
              /localhost:4000/,
              // Production Admin API (when CORS is configured for trace headers)
              /https:\/\/api\.impleo\.ai\/admin/,
              /https:\/\/api\.impleo\.ai/,
              // Note: Railway.app removed - doesn't allow traceparent/tracestate headers
            ],
          },
        }),
      ],

      // Session tracking configuration
      sessionTracking: {
        enabled: true,
        persistent: true,
        samplingRate: 1, // 100% of admin sessions tracked (lower volume)
      },

      // Batching configuration
      batching: {
        enabled: true,
        sendTimeout: 250,
        itemLimit: 50,
      },

      // Ignore errors from these sources
      ignoreErrors: [
        /chrome-extension/,
        /moz-extension/,
        /ResizeObserver loop/,
        /Loading chunk/,
        /Network request failed/,
        /Failed to fetch/,
      ],

      // Ignore URLs for fetch instrumentation
      ignoreUrls: [
        /posthog\.com/,
        /sentry\.io/,
        /grafana\.net/,
        // First-party analytics proxy (prevent recursion)
        /\/ingest\//,
        /chrome-extension/,
        /moz-extension/,
      ],

      // Before sending events - strict filtering for admin (GDPR + security)
      beforeSend: (event) => {
        // Drop events with sensitive admin URLs
        if (event.payload?.url) {
          const sensitivePatterns = [
            /password/i,
            /token/i,
            /secret/i,
            /credit/i,
            /user.*detail/i,
            /api_key/i,
          ];
          if (sensitivePatterns.some((p) => p.test(event.payload.url))) {
            return null;
          }
        }

        // Redact ALL request headers for admin (highly sensitive)
        if (event.payload?.request?.headers) {
          event.payload.request.headers = { note: '[REDACTED - Admin Request]' };
        }

        // Redact request body for admin
        if (event.payload?.request?.body) {
          event.payload.request.body = '[REDACTED - Admin Data]';
        }

        // Tag as admin panel
        event.meta = event.meta || {};
        event.meta.app_type = 'admin';

        return event;
      },
    });

    console.log('✅ Grafana Faro v2 initialized for Admin Panel');
    console.log(`📊 App: impleo-admin`);
    console.log(`🌍 Environment: ${import.meta.env.VITE_ENVIRONMENT || 'development'}`);

    return faroInstance;
  } catch (error) {
    console.error('❌ Failed to initialize Grafana Faro:', error);
    return null;
  }
};

/**
 * Get the Faro instance
 */
export const getFaro = () => faroInstance;

/**
 * Set admin user context for Faro events
 *
 * @param {Object} admin - Admin user object
 * @param {string} admin.id - Admin ID
 * @param {string} admin.email - Admin email
 * @param {string} admin.role - Admin role
 */
export const setFaroUser = (admin) => {
  if (!faroInstance) return;

  if (!admin) {
    faroInstance.api.setUser(null);
    return;
  }

  faroInstance.api.setUser({
    id: admin.id || admin.sub,
    email: admin.email,
    attributes: {
      role: admin.role,
      is_backup_auth: admin.is_backup_auth || false,
      app_type: 'admin',
    },
  });
};

/**
 * Clear admin user context
 */
export const clearFaroUser = () => {
  if (!faroInstance) return;
  faroInstance.api.setUser(null);
};

/**
 * Push custom admin event
 *
 * @param {string} name - Event name (use snake_case)
 * @param {Object} [attributes] - Event attributes
 */
export const pushFaroEvent = (name, attributes = {}) => {
  if (!faroInstance) return;
  faroInstance.api.pushEvent(name, {
    ...attributes,
    app_type: 'admin',
    timestamp: Date.now(),
  });
};

/**
 * Push custom measurement
 *
 * @param {string} name - Measurement name
 * @param {number} value - Measurement value
 * @param {Object} [attributes] - Additional attributes
 */
export const pushFaroMeasurement = (name, value, attributes = {}) => {
  if (!faroInstance) return;
  faroInstance.api.pushMeasurement({
    type: name,
    values: { value },
    ...attributes,
  });
};

/**
 * Log error to Faro
 *
 * @param {Error} error - Error to log
 * @param {Object} [context] - Additional context
 */
export const pushFaroError = (error, context = {}) => {
  if (!faroInstance) return;
  faroInstance.api.pushError(error, { context });
};

export default {
  initFaro,
  getFaro,
  setFaroUser,
  clearFaroUser,
  pushFaroEvent,
  pushFaroMeasurement,
  pushFaroError,
};
